import type { ApolloLink } from '@apollo/client';
import { GraphQLSchema, buildClientSchema, type IntrospectionQuery } from 'graphql';
import { withScalars } from 'apollo-link-scalars';
import { Temporal } from '../temporal';

export interface CreateTemporalLinkOptions {
  /**
   * Either a built GraphQLSchema, or an introspection query result
   * (e.g. JSON produced by @graphql-codegen/introspection).
   */
  schema: GraphQLSchema | IntrospectionQuery;
}

/**
 * Apollo Link that translates Temporal GraphQL scalars to/from
 * `Temporal.PlainDate` (for `Date`) and `Temporal.Instant` (for `DateTime`).
 *
 * Wire format: ISO-8601 strings (`2026-04-28` for Date, `2026-04-28T12:00:00Z`
 * for DateTime).
 *
 * - Outgoing variables: `Temporal.PlainDate` / `Temporal.Instant` → ISO string
 * - Incoming response data: ISO string → `Temporal.PlainDate` / `Temporal.Instant`
 *
 * Place this link before the terminating HttpLink in the chain.
 */
export function createTemporalLink({ schema }: CreateTemporalLinkOptions): ApolloLink {
  const resolvedSchema = schema instanceof GraphQLSchema ? schema : buildClientSchema(schema);

  return withScalars({
    schema: resolvedSchema,
    typesMap: {
      Date: {
        serialize: (parsed: unknown): string | null => {
          if (parsed === null || parsed === undefined) return null;
          if (parsed instanceof Temporal.PlainDate) return parsed.toString();
          if (typeof parsed === 'string') return parsed;
          throw new TypeError(`Date scalar: cannot serialize value of type ${typeof parsed}`);
        },
        parseValue: (raw: unknown): Temporal.PlainDate | null => {
          if (raw === null || raw === undefined) return null;
          if (raw instanceof Temporal.PlainDate) return raw;
          if (typeof raw === 'string') return Temporal.PlainDate.from(raw);
          throw new TypeError(`Date scalar: cannot parse value of type ${typeof raw}`);
        }
      },
      DateTime: {
        serialize: (parsed: unknown): string | null => {
          if (parsed === null || parsed === undefined) return null;
          if (parsed instanceof Temporal.Instant) return parsed.toString();
          if (typeof parsed === 'string') return parsed;
          throw new TypeError(`DateTime scalar: cannot serialize value of type ${typeof parsed}`);
        },
        parseValue: (raw: unknown): Temporal.Instant | null => {
          if (raw === null || raw === undefined) return null;
          if (raw instanceof Temporal.Instant) return raw;
          if (typeof raw === 'string') return Temporal.Instant.from(raw);
          throw new TypeError(`DateTime scalar: cannot parse value of type ${typeof raw}`);
        }
      }
    }
  });
}
